import type { AppColors } from './types';

/**
 * Dark palette. Functionally preserved — deep forest-tinted dark surfaces,
 * mint-tinted off-white foreground. Semantic meanings for
 * success/warning/danger/info are preserved; the warm amber is reserved
 * for warning contexts and the warm crimson for danger, so the user's
 * mental model of red = bad and amber = caution still holds.
 *
 * Brightness tuned for sober financial reading: no neon, no oversaturation.
 */
export const appColorsDark: AppColors = {
  semantic: {
    success: '#68ad7c',
    successStrong: '#478f5d',
    warning: '#c79052',
    warningStrong: '#a86f37',
    danger: '#c47786',
    dangerStrong: '#a34f61',
    info: '#7598bf',
    infoStrong: '#557bab',
    violet: '#9988b5',
    cyan: '#609ba5',
    slate: '#899aa6',
    white: '#ffffff',
  },
  actions: {
    accentOutline: {
      text: '#c79052',
      border: '#c7905266',
      hoverText: '#d5aa68',
      hoverBorder: '#d5aa6899',
      hoverBg: '#c790521f',
    },
    range: {
      selectedBg: '#68ad7c',
      selectedBorder: '#68ad7c',
      selectedText: '#0a140d',
      selectedHoverBg: '#478f5d',
      selectedHoverBorder: '#478f5d',
      unselectedText: '#9fb3a4',
      unselectedBorder: '#b8c8bc59',
      unselectedHoverText: '#68ad7c',
      unselectedHoverBorder: '#68ad7c99',
      unselectedHoverBg: '#68ad7c1f',
    },
    compareButton: {
      bg: '#7fa98a',
      hoverBg: '#68ad7c',
      text: '#0a140d',
    },
  },
  charts: {
    market: {
      ifix: '#68ad7c',
      ibov: '#9988b5',
      idiv: '#609ba5',
      cdi: '#c79052',
      selicMeta: '#d5aa68',
      ipcaMeta: '#7fa98a',
      selicEfetiva12m: '#c47786',
      ipca12m: '#7598bf',
      igpm: '#609ba5',
      pib: '#9988b5',
      dlsp: '#609ba5',
      dbgg: '#b0bec4',
      ifixBrl: '#68ad7c',
      ifixUsd: '#7fa98a',
      ifixEur: '#c79052',
      ifixGbp: '#9988b5',
      ivvbBrl: '#68ad7c',
      ivvbUsd: '#7fa98a',
    },
    summaryHistory: {
      price: '#c79052',
      vp: '#7598bf',
      dy: '#68ad7c',
      pvp: '#9988b5',
      benchmark: '#b0bec4',
    },
    portfolioMix: {
      realEstate: '#68ad7c',
      funds: '#7fa98a',
      financialAssets: '#c79052',
      equities: '#9988b5',
    },
  },
  indicators: {
    label: {
      high: '#c47786',
      medium: '#c79052',
      low: '#7fa98a',
      muted: 'hsl(var(--muted-foreground))',
    },
    distance: {
      near: '#7fa98a',
      medium: '#c79052',
      far: '#7598bf',
      muted: 'hsl(var(--muted-foreground))',
    },
    accent: {
      selic: '#7598bf',
      leverage: '#c79052',
      distance: '#9988b5',
      vacancy: '#609ba5',
      delinquency: '#c47786',
    },
  },
  highlights: {
    topBorder: {
      primary: '#68ad7c',
      success: '#7fa98a',
      warning: '#c79052',
      purple: '#9988b5',
    },
    progressBar: {
      primary: '#68ad7c',
      warning: '#c79052',
    },
  },
  modules: {
    comparadorSeries: ['#68ad7c', '#7598bf', '#7fa98a', '#c79052', '#9988b5'],
    priceHistorySeries: ['#b0bec4', '#7fa98a', '#c79052', '#609ba5', '#9988b5', '#c47786'],
    simulation: {
      price: '#c79052',
      reinvested: '#68ad7c',
      shares: '#9988b5',
      adjustedPrice: '#b0bec4',
      dividends: '#7598bf',
      eventMarker: '#9988b5',
    },
    liquidity: {
      liquidity: '#609ba5',
      cotistas: '#7598bf',
      cotas: '#68ad7c',
    },
    dividends: {
      average: '#899aa6',
      price: '#7598bf',
      dy: '#68ad7c',
      dyGuide: '#7fa98a',
      eventMarker: '#c79052',
      eventMarkerHover: '#d5aa68',
      corporateEventLine: '#899aa6',
      corporateEventMarker: '#b0bec4',
    },
    risk: {
      good: '#68ad7c',
      medium: '#c79052',
      bad: '#c47786',
      needle: '#c79052',
      needleBorder: '#68ad7c',
    },
  },
};
